import * as moment from 'moment';
import { Action, createReducer, on } from '@ngrx/store';
import {
    EVENTS_PAGE_DEFAULT_SIZE, EVENTS_DEFAULT_SORT_COLUMN, EVENTS_DEFAULT_SORT_COLUMN_DIRECTION,
    EVENTS_MAX_DAYS_BACK
} from 'src/app/conf/events';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { EventFilterParams } from 'src/app/model/api/filter-params';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { EventMessage, EventType } from 'src/app/model/event-message';
import { MailNotificationConfig } from 'src/app/model/mail-notification-config';
import { ManeuverNotificationConfig } from 'src/app/model/maneuver-notification-config';

import * as EventActions from './event.actions';

export interface State {
    errorMessage: string;
    eventsLoading: boolean;
    paging?: ItemsPagination;
    events: EventMessage[];
    eventTypes: EventType[];
    emailConfigs: MailNotificationConfig[];
    maneuverConfigs: ManeuverNotificationConfig[];
    pagination: PaginationParams;
    filter: EventFilterParams;
    maneuverNotifCreating: boolean;
    maneuverNotifCreated: boolean;
    maneuverNotifUpdating: boolean;
    maneuverNotifUpdated: boolean;
    maneuverNotifDeleting: boolean;
    maneuverNotifDeleted: boolean;
    eventsAllLoading: boolean;
    pagingAll?: ItemsPagination;
    eventsAll: EventMessage[];
}
export const initialState: State = {
    errorMessage: '',
    eventsLoading: false,
    events: [],
    eventTypes: [],
    emailConfigs: [],
    maneuverConfigs: [],
    pagination: {
        size: EVENTS_PAGE_DEFAULT_SIZE,
        page: 1,
        sort: EVENTS_DEFAULT_SORT_COLUMN,
        order: EVENTS_DEFAULT_SORT_COLUMN_DIRECTION,
    },
    filter: {
        endDate: new Date(),
        startDate: moment(new Date()).subtract(EVENTS_MAX_DAYS_BACK, 'days').toDate(),
    },
    maneuverNotifCreating: false,
    maneuverNotifCreated: false,
    maneuverNotifUpdating: false,
    maneuverNotifUpdated: false,
    maneuverNotifDeleting: false,
    maneuverNotifDeleted: false,
    eventsAllLoading: false,
    eventsAll: [],
};

const eventReducer = createReducer(
    initialState,
    on(EventActions.getEvents, (state) => ({ ...state, eventsLoading: true })),
    on(EventActions.getEventsSuccess, (state, payload) => ({
        ...state,
        paging: payload.paging,
        events: payload.events,
        eventsLoading: false
    })),
    on(EventActions.getEventsAllReset, (state) => ({
        ...state, eventsAllLoading: false, pagingAll: null,
        eventsAll: []
    })),
    on(EventActions.getEventsAll, (state) => ({ ...state, eventsAllLoading: true })),
    on(EventActions.getEventsAllSuccess, (state, payload) => ({
        ...state,
        pagingAll: payload.paging,
        eventsAll: payload.events,
        eventsAllLoading: false
    })),
    on(EventActions.getEventsAllFailed, (state) => ({ ...state, eventsAllLoading: false })),
    on(EventActions.setPagination, (state, payload) => {
        const newPagination = { ...state.pagination };
        if ('page' in payload) {
            newPagination.page = payload.page;
        }
        if ('size' in payload) {
            newPagination.size = payload.size;
        }
        if ('order' in payload) {
            newPagination.order = payload.order;
        }
        if ('sort' in payload) {
            newPagination.sort = payload.sort;
        }
        return { ...state, pagination: newPagination };
    }),
    on(EventActions.resetFilter, (state) => {
        return {
            ...state, filter: {
                endDate: new Date(),
                startDate: moment(new Date()).subtract(EVENTS_MAX_DAYS_BACK, 'days').toDate(),
            }
        }
    }),
    on(EventActions.setFilter, (state, payload) => {
        const newFilter = { ...state.filter };
        if ('deviceId' in payload) {
            newFilter.deviceId = payload.deviceId;
        }
        if ('typeId' in payload) {
            newFilter.typeId = payload.typeId;
        }
        if ('startDate' in payload) {
            newFilter.startDate = payload.startDate;
        }
        if ('endDate' in payload) {
            newFilter.endDate = payload.endDate;
        }
        return { ...state, filter: newFilter };
    }),
    on(EventActions.getEventsFailed, (state, payload) => ({ ...state, errorMessage: payload.errorMessage, eventsLoading: false })),
    on(EventActions.getEventTypesSuccess, (state, payload) => ({ ...state, eventTypes: payload.eventTypes })),
    on(EventActions.getEventsFailed, (state, payload) => ({ ...state, errorMessage: payload.errorMessage })),
    on(EventActions.getEmailConfigsSuccess, (state, payload) => ({ ...state, emailConfigs: payload.emailConfigs })),
    on(EventActions.getManeuversNotifConfigsSuccess, (state, payload) => ({ ...state, maneuverConfigs: payload.maneuverConfigs })),
    on(EventActions.notificationDialogReset, (state) => ({
        ...state,
        maneuverNotifCreating: false, maneuverNotifCreated: false, maneuverNotifUpdating: false,
        maneuverNotifUpdated: false, maneuverNotifDeleting: false, maneuverNotifDeleted: false,
    })),
    on(EventActions.createManeuversNotifConfigSuccess, (state, payload) => ({
        ...state,
        maneuverConfigs: [payload.config], maneuverNotifCreated: true,
    })),
    on(EventActions.updateManeuversNotifConfigSuccess, (state, payload) => ({
        ...state,
        maneuverConfigs: [payload.config], maneuverNotifUpdated: true,
    })),
    on(EventActions.saveEmailConfigsSuccess, (state) => ({
        ...state,
        maneuverNotifUpdated: true,
    })),
    on(EventActions.deleteEmailConfigsSuccess, (state) => ({
        ...state,
        maneuverNotifUpdated: true,
    })),
    on(EventActions.deleteManeuversNotifConfigSuccess, (state) => ({
        ...state,
        maneuverConfigs: [], maneuverNotifDeleted: true,
    })),
);

export const reducer = (state: State | undefined, action: Action): State => eventReducer(state, action);
